import type {
    AtLeastOneRule,
    AtMostInputRule,
    ExclusiveOptionRule,
    ExclusiveRule,
    PageValidation,
    PageValidationFailure,
    PageValidationRule,
    PatternRule,
    SumEqualsRule,
} from "./types";

type AnswerValues = Readonly<Record<string, unknown>>;

function isSumEqualsRule(raw: unknown): raw is SumEqualsRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<SumEqualsRule>;
    return (
        rule.type === "sum_equals" &&
        Array.isArray(rule.input_ids) &&
        rule.input_ids.length > 0 &&
        rule.input_ids.every((id) => typeof id === "string" && id !== "") &&
        typeof rule.total === "number" &&
        Number.isFinite(rule.total) &&
        (rule.layout === undefined || rule.layout === "image_counts") &&
        (rule.message === undefined || typeof rule.message === "string")
    );
}

function isAtMostInputRule(raw: unknown): raw is AtMostInputRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<AtMostInputRule>;
    return (
        rule.type === "at_most_input" &&
        typeof rule.input_id === "string" &&
        rule.input_id !== "" &&
        typeof rule.max_input_id === "string" &&
        rule.max_input_id !== "" &&
        (rule.message === undefined || typeof rule.message === "string")
    );
}

function isExclusiveRule(raw: unknown): raw is ExclusiveRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<ExclusiveRule>;
    return (
        rule.type === "exclusive" &&
        typeof rule.exclusive_input_id === "string" &&
        rule.exclusive_input_id !== "" &&
        Array.isArray(rule.input_ids) &&
        rule.input_ids.length > 0 &&
        rule.input_ids.every((id) => typeof id === "string" && id !== "" && id !== rule.exclusive_input_id) &&
        (rule.message === undefined || typeof rule.message === "string")
    );
}

function isExclusiveOptionRule(raw: unknown): raw is ExclusiveOptionRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<ExclusiveOptionRule>;
    return (
        rule.type === "exclusive_option" &&
        typeof rule.input_id === "string" &&
        rule.input_id !== "" &&
        ((typeof rule.value === "string" && rule.value !== "") ||
            (typeof rule.value === "number" && Number.isFinite(rule.value))) &&
        (rule.message === undefined || typeof rule.message === "string")
    );
}

function isAtLeastOneRule(raw: unknown): raw is AtLeastOneRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<AtLeastOneRule>;
    return (
        rule.type === "at_least_one" &&
        Array.isArray(rule.input_ids) &&
        rule.input_ids.length > 0 &&
        rule.input_ids.every((id) => typeof id === "string" && id !== "") &&
        (rule.message === undefined || typeof rule.message === "string")
    );
}

/** The rule's regular expression, or null when it does not compile. */
function compilePattern(rule: Pick<PatternRule, "pattern" | "flags">): RegExp | null {
    try {
        return new RegExp(rule.pattern, rule.flags);
    } catch {
        return null;
    }
}

function isPatternRule(raw: unknown): raw is PatternRule {
    if (!raw || typeof raw !== "object") return false;
    const rule = raw as Partial<PatternRule>;
    return (
        rule.type === "pattern" &&
        typeof rule.input_id === "string" &&
        rule.input_id !== "" &&
        typeof rule.pattern === "string" &&
        rule.pattern !== "" &&
        (rule.flags === undefined || typeof rule.flags === "string") &&
        typeof rule.message === "string" &&
        rule.message !== "" &&
        compilePattern({ pattern: rule.pattern, flags: rule.flags }) !== null
    );
}

function isKnownRule(raw: unknown): raw is PageValidationRule {
    return (
        isSumEqualsRule(raw) ||
        isAtMostInputRule(raw) ||
        isExclusiveRule(raw) ||
        isExclusiveOptionRule(raw) ||
        isAtLeastOneRule(raw) ||
        isPatternRule(raw)
    );
}

/** A ticked checkbox, as the form holds it (true) or as it is saved ("true"). */
export function isTicked(value: unknown): boolean {
    return value === true || value === "true" || value === 1;
}

/** A multiple-choice answer as its list of saved values (as text). Empty for a blank answer. */
export function choiceValues(value: unknown): string[] {
    if (Array.isArray(value)) return value.map(String);
    if (value === null || value === undefined || value === "") return [];
    return [String(value)];
}

/** Reads `pages.page_validation`. Rules of an unknown type or shape are dropped, never guessed at. */
export function parsePageValidation(raw: unknown): PageValidation | null {
    if (!raw || typeof raw !== "object") return null;
    const rules = (raw as { rules?: unknown }).rules;
    if (!Array.isArray(rules)) return null;
    const known = rules.filter(isKnownRule);
    return known.length > 0 ? { rules: known } : null;
}

/** A finite number from a number or numeric string, or null for a blank or non-numeric answer. */
function toFiniteNumberOrNull(value: unknown): number | null {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function toNumberOrZero(value: unknown): number {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

/** The running total of a sum rule's inputs. A blank or non-numeric answer counts as 0. */
export function computeGroupSum(rule: SumEqualsRule, values: AnswerValues): number {
    return rule.input_ids.reduce((sum, inputId) => sum + toNumberOrZero(values[inputId]), 0);
}

function checkRule(rule: PageValidationRule, values: AnswerValues): PageValidationFailure | null {
    switch (rule.type) {
        case "sum_equals":
            if (computeGroupSum(rule, values) === rule.total) return null;
            return { rule, input_id: rule.input_ids[rule.input_ids.length - 1]! };
        case "exclusive":
            if (!isTicked(values[rule.exclusive_input_id])) return null;
            if (!rule.input_ids.some((id) => isTicked(values[id]))) return null;
            return { rule, input_id: rule.exclusive_input_id };
        case "exclusive_option": {
            const picked = choiceValues(values[rule.input_id]);
            if (!picked.includes(String(rule.value)) || picked.length < 2) return null;
            return { rule, input_id: rule.input_id };
        }
        case "at_least_one":
            if (rule.input_ids.some((id) => isTicked(values[id]))) return null;
            return { rule, input_id: rule.input_ids[0]! };
        case "at_most_input": {
            const value = toFiniteNumberOrNull(values[rule.input_id]);
            const max = toFiniteNumberOrNull(values[rule.max_input_id]);
            if (value === null || max === null || value <= max) return null;
            return { rule, input_id: rule.input_id };
        }
        case "pattern": {
            const raw = values[rule.input_id];
            const text = typeof raw === "string" ? raw.trim() : "";
            if (text === "" || compilePattern(rule)?.test(text)) return null;
            return { rule, input_id: rule.input_id };
        }
    }
}

/**
 * Every rule on the page that the current answers break. Empty when the page is valid.
 * `values` may include answers from earlier pages, for rules that compare against them.
 */
export function evaluatePageValidation(
    validation: PageValidation | null | undefined,
    values: AnswerValues
): PageValidationFailure[] {
    if (!validation) return [];
    return validation.rules.flatMap((rule) => {
        const failure = checkRule(rule, values);
        return failure ? [failure] : [];
    });
}

/** The sum rule whose running total is shown under this input, which is the last input of the group. */
export function findSumRuleEndingAt(
    validation: PageValidation | null | undefined,
    inputId: string
): SumEqualsRule | null {
    const rule = validation?.rules.find(
        (candidate) =>
            candidate.type === "sum_equals" && candidate.input_ids[candidate.input_ids.length - 1] === inputId
    );
    return rule?.type === "sum_equals" ? rule : null;
}

/** The sum rule that asks for the image and count layout, or null when the page has none. */
export function findImageCountsRule(validation: PageValidation | null | undefined): SumEqualsRule | null {
    const rule = validation?.rules.find(
        (candidate) => candidate.type === "sum_equals" && candidate.layout === "image_counts"
    );
    return rule?.type === "sum_equals" ? rule : null;
}

/**
 * How far a sum group is from its target: how many are still to place, how many are over, or neither
 * when it is exact. Never both.
 */
export function sumGap(total: number, target: number): { left: number; over: number } {
    return { left: Math.max(target - total, 0), over: Math.max(total - target, 0) };
}

/**
 * The checkboxes to untick after `changedInputId` was ticked: the others of its list when it is the
 * exclusive one, or the exclusive one when it is one of the others. Empty when nothing has to change.
 */
export function checkboxesToUntick(
    validation: PageValidation | null | undefined,
    changedInputId: string,
    values: Readonly<Record<string, unknown>>
): string[] {
    if (!validation || !isTicked(values[changedInputId])) return [];
    const untick = new Set<string>();
    for (const rule of validation.rules) {
        if (rule.type !== "exclusive") continue;
        if (rule.exclusive_input_id === changedInputId) {
            for (const id of rule.input_ids) if (isTicked(values[id])) untick.add(id);
        } else if (rule.input_ids.includes(changedInputId) && isTicked(values[rule.exclusive_input_id])) {
            untick.add(rule.exclusive_input_id);
        }
    }
    return [...untick];
}

/**
 * A multiple-choice answer after a change, with the page's exclusive_option rules applied: picking an exclusive
 * choice clears the others, and picking any other choice clears the exclusive one. `previous` is the answer
 * before the change, so the choice just picked wins. Returned unchanged when no rule applies.
 */
export function applyExclusiveOptions(
    validation: PageValidation | null | undefined,
    inputId: string,
    previous: readonly string[],
    next: readonly string[]
): string[] {
    const exclusive = new Set(
        (validation?.rules ?? []).flatMap((rule) =>
            rule.type === "exclusive_option" && rule.input_id === inputId ? [String(rule.value)] : []
        )
    );
    if (exclusive.size === 0) return [...next];
    const added = next.filter((value) => !previous.includes(value));
    const addedExclusive = added.find((value) => exclusive.has(value));
    if (addedExclusive !== undefined) return [addedExclusive];
    if (added.length > 0) return next.filter((value) => !exclusive.has(value));
    return [...next];
}
